import React from "react";
import diff from "json-patch-gen";
import { RouteComponentProps } from "react-router-dom";
import { Button, SectionContainer } from "component-library";
import { IVideoModel } from "shared-types";
import { getLocalUrl } from "common";
import { GAEvents } from "mediaModules/media/components/GAEvents";
import { VideoPlayerContainer } from "mediaModules/media/components/VideoPlayerContainer";
import { MediaAsset, MediaAssetType } from "mediaModules/media/models";
import mediaService from "mediaModules/media/services/MediaService";
import { scrollRefToTop } from "mediaModules/media/utils/page";

import {
  IGlobalContextProps,
  withGlobalContext,
} from "shared/contexts/GlobalContext";
import { AlertConfigBuilder } from "shared/utils/alert-config-builder";

import { GlobalEventTypes } from "contexts/GlobalContext";
import {
  ITranslationContextProps,
  Translator,
  withTranslation,
} from "contexts/TranslationContext";

import {
  Breadcrumbs,
  IBreadcrumbsContent,
} from "components/common/Breadcrumbs";

import { EditVideoForm } from "./components/EditVideoForm";
import {
  getSectionContainerProps,
  IEditVideoPageContent,
  IEditVideoPageContentKeys,
} from "./EditVideoPageContent";

import "./EditVideoPage.scss";
import { IActionButtonProps } from "component-library/src/components/Atoms/ActionButton/ActionButton";
import { IBreadcrumbsContentKeys } from "components/common/Breadcrumbs/Breadcrumbs";
import { authorizationService } from "shared-auth";
import { captureExceptionEvent } from "shared-services";
import { dataTestIds } from "data-testids";

export interface IEditVideoPageMatchParams {
  id: string;
}

export interface IEditVideoPageProps
  extends ITranslationContextProps,
    IGlobalContextProps,
    RouteComponentProps<IEditVideoPageMatchParams> {}

export interface IEditVideoPageState {
  content: IEditVideoPageContent;
  videoModel: MediaAsset;
  isMediaMetadataLoading: boolean;
}

export class EditVideoPage extends React.Component<
  IEditVideoPageProps,
  IEditVideoPageState
> {
  private pageRef: React.RefObject<HTMLDivElement> =
    React.createRef<HTMLDivElement>();
  private initialModelSnapshot: string;

  constructor(props: Readonly<IEditVideoPageProps>) {
    super(props);

    this.state = {
      content: EditVideoPage.mapTranslation(props.translator),
      videoModel: {
        ...new MediaAsset(),
        thumbnails: [],
        isMediaObjectLoading: true,
      },
      isMediaMetadataLoading: true,
    };
  }

  public render() {
    const { content, videoModel, isMediaMetadataLoading } = this.state;

    const breadcrumbsContent: IBreadcrumbsContent =
      this.getBreadcrumbsTranslation();
    const sectionContainerProps = getSectionContainerProps(
      content,
      this.handleCloseClick
    );

    return (
      <div ref={this.pageRef}>
        <Breadcrumbs content={breadcrumbsContent} />
        <div className="g-content-area p-edit-video-page">
          <div className="g-col-lg-12 p-edit-video-page__container">
            <SectionContainer {...sectionContainerProps}>
              <div className="p-edit-video-page__content">
                <EditVideoForm
                  key={videoModel.id}
                  content={content.editVideoForm}
                  videoModel={videoModel}
                  changeModel={this.changeModel}
                  saveChanges={this.saveChanges}
                  showMediaPopup={this.showMediaPopup}
                  isMediaMetadataLoading={isMediaMetadataLoading}
                >
                  {this.renderFormButtons}
                </EditVideoForm>
              </div>
            </SectionContainer>
          </div>
        </div>
      </div>
    );
  }

  public static getDerivedStateFromProps(
    nextProps: IEditVideoPageProps,
    prevState: IEditVideoPageState
  ) {
    if (
      nextProps.translator.version !==
      nextProps.translator.getTranslationVersion(prevState.content)
    ) {
      const content = EditVideoPage.mapTranslation(nextProps.translator);

      return { content };
    }

    return null;
  }

  public async componentDidMount() {
    try {
      GAEvents.trackPageLoad(
        "Edit individual video page is loaded",
        MediaAssetType.Video
      );

      await this.getMediaMetadata();
    } catch (error) {
      captureExceptionEvent(error, authorizationService.getDecodedUserToken());
    }
  }

  private getMediaMetadata = async () => {
    const mediaMetadata = await mediaService.getMediaMetadataById(
      this.props.match.params.id
    );
    const mediaAssetPreLoad =
      mediaService.createMediaAssetFromMeta(mediaMetadata);

    this.setState({
      videoModel: mediaAssetPreLoad,
      isMediaMetadataLoading: false,
    });

    await this.getMediaAsset(mediaAssetPreLoad);
  };

  private getMediaAsset = async (videoModel: MediaAsset) => {
    const mediaObject = await mediaService.getMediaObjectById(
      videoModel.mediaInfoId
    );
    const mediaAsset = mediaService.mapUpdatedMediaObject(
      videoModel,
      mediaObject
    );

    this.setState({ videoModel: mediaAsset });
    this.setInitialModelSnapshot(mediaAsset);
  };

  private renderFormButtons = (submit: () => void) => {
    const {
      content,
      videoModel: { isMediaObjectLoading },
    } = this.state;

    return (
      <div className="p-edit-video-page__buttons">
        <div className="p-edit-video-page__button">
          <Button
            type="primary"
            onClick={this.handleSaveClick(submit)}
            text={content.saveButton}
            disabled={isMediaObjectLoading}
            data-testid={`${dataTestIds.componentLibrary["Atoms.Button.Link"]}-${dataTestIds.profile["Media.MediaForm.SaveButton"]}`}
          />
        </div>
        <div className="p-edit-video-page__button">
          <Button
            type="secondary"
            onClick={this.handleCancelAndCloseClick}
            text={content.cancelButton}
          />
        </div>
      </div>
    );
  };

  private changeModel = (videoModel: MediaAsset) =>
    this.setState({ videoModel });

  private setInitialModelSnapshot = (mediaAsset: MediaAsset) => {
    this.initialModelSnapshot = JSON.stringify(mediaAsset);
  };

  private isModelChanged = () =>
    this.initialModelSnapshot !== JSON.stringify(this.state.videoModel);

  private handleSaveClick = (submit: () => void) => () => {
    GAEvents.editMediaAsset.trackSaveClick(MediaAssetType.Video);
    submit();
  };

  private handleCancelAndCloseClick = () => {
    GAEvents.editMediaAsset.trackCancelCloseClick(MediaAssetType.Video);
    this.closePage();
  };

  private handleCloseClick = () => {
    GAEvents.editMediaAsset.trackCloseClick(MediaAssetType.Video);
    this.closePage();
  };

  private closePage = () => {
    const {
      content: { cancelPopup },
    } = this.state;
    const { globalContext } = this.props;

    if (this.isModelChanged()) {
      const message = new AlertConfigBuilder()
        .initBuildEntity()
        .setDefaultContent({
          closeButtonText: cancelPopup.continueEditingButtonText,
          description: cancelPopup.cancelPopupDescription,
          title: cancelPopup.cancelPopupTitle,
        })
        .setCloseButton({
          type: "secondary",
          click: () =>
            globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert),
        })
        .addButton({
          type: "primary",
          click: () => {
            globalContext.notifyListener(GlobalEventTypes.closeAllGlobalAlert);
            this.redirectToEditMediaAssets();
          },
          name: cancelPopup.cancelPopupCloseButtonText,
        })
        .build();

      globalContext.notifyListener(
        GlobalEventTypes.notifyingGlobalAlert,
        message
      );
    } else {
      this.redirectToEditMediaAssets();
    }
  };

  private saveChanges = async () => {
    const initialModel = JSON.parse(this.initialModelSnapshot);
    const { title, selectedThumbnail } = this.state.videoModel;

    const difference = diff(
      {
        title: initialModel.title,
        selectedThumbnail: initialModel.selectedThumbnail,
      },
      {
        title,
        selectedThumbnail: String(selectedThumbnail),
      }
    );

    if (difference.length) {
      try {
        await mediaService.updateMediaMetadata(
          this.props.match.params.id,
          difference
        );
      } catch (error) {
        captureExceptionEvent(
          error,
          authorizationService.getDecodedUserToken()
        );
      }
    }

    this.redirectToEditMediaAssets();
  };

  private redirectToEditMediaAssets = () => {
    scrollRefToTop(this.pageRef);
    this.props.history.push(getLocalUrl("media/video"));
  };

  private showMediaPopup = (videoModel: MediaAsset) => {
    const {
      id,
      durationInMs,
      sources,
      title,
      thumbnails,
      selectedThumbnail,
      downloadLink,
    } = videoModel;
    const controls = this.getPlayerPanel(videoModel);

    const video: IVideoModel = {
      id,
      sources,
      duration: durationInMs,
      fileName: title,
      previewImg: thumbnails[selectedThumbnail],
      poster: thumbnails[0],
      shareLink: downloadLink,
      downloadLink,
    };

    const videoPlayer = (
      <VideoPlayerContainer
        playerConfig={{
          autoPlay: true,
          hasCloseButton: true,
          videoList: [video],
          selectedVideo: video,
          selectVideo: () => undefined,
          close: this.closeMediaPopup,
        }}
        controlPanelButtons={controls}
      />
    );

    const popupProps = {
      hideCloseButton: true,
      fullHeight: true,
      className: "c-thumbnail-video-viewer__popup",
    };

    this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content: videoPlayer,
      popupProps,
    });
  };

  private getPlayerPanel = (showreel: MediaAsset) => {
    const { downloadButton } = this.state.content.editVideoForm;

    const buttons: (IActionButtonProps & { key: string | number })[] = [];
    buttons.push({
      key: "download1",
      icon: "download1",
      label: downloadButton,
      onClick: () => {
        GAEvents.editMediaAsset.trackDownloadClick(MediaAssetType.Video);
        window.location.href = showreel.downloadLink;
        this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
          content: null,
        });
      },
    });

    return buttons;
  };

  private closeMediaPopup = () => {
    this.props.globalContext.notifyListener(GlobalEventTypes.showPopup, {
      content: null,
    });
  };

  private getBreadcrumbsTranslation = () =>
    this.props.translator
      .getTranslator("breadcrumbs", true)
      .createTranslationObject<IBreadcrumbsContent>(IBreadcrumbsContentKeys);

  private static mapTranslation = (translator: Translator) =>
    translator.compileTranslationObject<IEditVideoPageContent>(
      IEditVideoPageContentKeys,
      "common"
    );
}

export default withTranslation(
  withGlobalContext(EditVideoPage),
  "editVideoClipPage"
);
